import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Package, Plus, Upload } from 'lucide-react';
import { PROCUREMENT_STATUS, money } from '@adisys/shared';
import { api, downloadCsv } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { errorText, qtyLabel } from '../../../lib/crm';
import { Column, DataTable, FilterBar, FilterSelect, SearchInput } from '../../../components/DataTable';
import { Button, StatusBadge, cx, useToast } from '../../../components/ui';
import { ImportModal, ProductDetailModal, ProductFormModal, StockAdjustModal } from './ProductModals';
import { Thumb } from './Thumb';

type Status = keyof typeof PROCUREMENT_STATUS.byValue;

export function ProductsTab() {
  const { can } = useAuth();
  const toast = useToast();
  const manage = can('crm.catalog.manage');

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [stockStatus, setStockStatus] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });

  const [detailId, setDetailId] = useState<string | null>(null);
  const [form, setForm] = useState<{ product: any | null } | null>(null);
  const [adjust, setAdjust] = useState<any | null>(null);
  const [importing, setImporting] = useState(false);

  const cats = useQuery({ queryKey: ['crm', 'catalog', 'categories'], queryFn: () => api.get('/crm/categories') });
  const vendors = useQuery({
    queryKey: ['crm', 'catalog', 'vendors', 'all'],
    queryFn: () => api.get('/crm/vendors'),
    enabled: can('crm.catalog.view', 'crm.procurement.view'),
  });
  const categories: any[] = cats.data?.data ?? [];
  const selectedCat = categories.find((c) => c.id === categoryId);

  const filters = {
    q: search || undefined, categoryId: categoryId || undefined, subcategoryId: subcategoryId || undefined,
    brandId: brandId || undefined, vendorId: vendorId || undefined, stockStatus: stockStatus || undefined,
  };
  const query = useQuery({
    queryKey: ['crm', 'catalog', 'products', filters, page, sort],
    queryFn: () => api.get('/crm/products', { ...filters, page, size: 25, sort: sort.key, dir: sort.dir }),
    placeholderData: (prev) => prev,
  });
  const showsCost = Boolean(query.data?.showsCost);

  const reset = () => {
    setSearch(''); setCategoryId(''); setSubcategoryId(''); setBrandId(''); setVendorId(''); setStockStatus(''); setPage(1);
  };
  const pickCategory = (id: string) => { setCategoryId(id); setSubcategoryId(''); setBrandId(''); setPage(1); };

  const columns = useMemo<Array<Column<any>>>(() => {
    const cols: Array<Column<any>> = [
      { key: 'name', header: 'Product', sortable: true, render: (p) => (
        <div className="flex min-w-0 items-center gap-3">
          <Thumb fileId={p.imageFileId} size={40} />
          <div className="min-w-0">
            <p className="truncate font-medium text-ink-900">{p.name}</p>
            <p className="tabular truncate text-xs text-ink-500">{p.sku}{p.modelNumber ? ` · ${p.modelNumber}` : ''}</p>
          </div>
        </div>) },
      { key: 'categoryName', header: 'Category', sortable: true, hideBelow: 'md', render: (p) => (
        <div className="text-xs"><p className="text-ink-800">{p.categoryName}</p><p className="text-ink-500">{p.subcategoryName ?? ''}</p></div>) },
      { key: 'brand', header: 'Brand / vendor', hideBelow: 'lg', render: (p) => (
        <div className="text-xs"><p className="text-ink-800">{p.brandName ?? '—'}</p><p className="text-ink-500">{p.vendorName ?? ''}</p></div>) },
      { key: 'sellingPrice', header: 'Price', numeric: true, sortable: true, render: (p) => (
        <div><p className="font-medium text-ink-900">{money(p.sellingPrice)}</p><p className="text-xs text-ink-500">GST {p.gstRate}%</p></div>) },
    ];
    if (showsCost) {
      cols.push({ key: 'cost', header: 'Cost / margin', numeric: true, hideBelow: 'xl', render: (p) => (
        p.costPrice === undefined ? null :
        <div><p className="text-ink-800">{money(p.costPrice)}</p><p className="text-xs text-ink-500">{p.marginPct}% margin</p></div>) });
    }
    cols.push(
      { key: 'available', header: 'Available', numeric: true, sortable: true, render: (p) => (
        <div><p className={cx('font-medium', Number(p.available) <= 0 ? 'text-danger' : 'text-ink-900')}>{qtyLabel(p.available)} {p.unit}</p>
          {Number(p.stockReserved) > 0 && <p className="text-xs text-ink-500">{qtyLabel(p.stockReserved)} reserved</p>}</div>) },
      { key: 'status', header: 'Status', render: (p) => {
        const m = PROCUREMENT_STATUS.byValue[p.procurementStatus as Status];
        return <div className="flex flex-col items-start gap-1"><StatusBadge tone={m.tone} title={m.description}>{m.label}</StatusBadge>
          {!p.active && <span className="text-xs text-ink-400">Inactive</span>}</div>;
      } },
    );
    return cols;
  }, [showsCost]);

  const doExport = () =>
    downloadCsv('/crm/products/export', filters, 'adisys-product-catalogue.csv')
      .catch((e) => toast.error('Export failed', errorText(e)));

  return (
    <div className="space-y-3">
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Solution areas">
          <button onClick={() => pickCategory('')}
            className={cx('rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors',
              !categoryId ? 'bg-ink-900 text-white ring-ink-900' : 'bg-white text-ink-700 ring-line-strong hover:bg-ink-50')}>All</button>
          {categories.filter((c) => c.active).map((c) => (
            <button key={c.id} onClick={() => pickCategory(c.id)}
              className={cx('rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors',
                categoryId === c.id ? 'bg-ink-900 text-white ring-ink-900' : 'bg-white text-ink-700 ring-line-strong hover:bg-ink-50')}>
              {c.name} <span className="opacity-60">{c.productCount}</span>
            </button>))}
        </div>)}

      <DataTable mobile="cards"
        columns={columns} rows={query.data?.data ?? []} rowKey={(p) => p.id}
        onRowClick={(p) => setDetailId(p.id)}
        loading={query.isLoading} error={query.error} onRetry={() => query.refetch()}
        sort={sort} onSort={(key) => { setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' })); setPage(1); }}
        page={query.data?.page} onPage={setPage}
        empty={{ icon: <Package className="h-5 w-5" />,
          title: Object.values(filters).some(Boolean) ? 'No products match these filters' : 'The catalogue is empty',
          description: manage ? 'Add products one by one, or import a CSV.' : undefined,
          action: manage ? <Button variant="primary" size="sm" onClick={() => setForm({ product: null })}>New product</Button> : undefined }}
        toolbar={
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <FilterBar active={Object.values(filters).filter(Boolean).length} onReset={reset}>
                <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="SKU, name, model or spec" />
                <FilterSelect label="Category" value={categoryId} onChange={pickCategory} allLabel="All categories"
                  options={categories.map((c) => ({ value: c.id, label: c.name }))} />
                <FilterSelect label="Subcategory" value={subcategoryId} onChange={(v) => { setSubcategoryId(v); setPage(1); }} allLabel="All subcategories"
                  options={(selectedCat?.subcategories ?? []).map((s: any) => ({ value: s.id, label: s.name }))} />
                <FilterSelect label="Brand" value={brandId} onChange={(v) => { setBrandId(v); setPage(1); }} allLabel="All brands"
                  options={(selectedCat?.brands ?? []).map((b: any) => ({ value: b.id, label: b.name }))} />
                <FilterSelect label="Vendor" value={vendorId} onChange={(v) => { setVendorId(v); setPage(1); }} allLabel="All vendors"
                  options={(vendors.data?.data ?? []).map((v: any) => ({ value: v.id, label: v.name }))} />
                <FilterSelect label="Stock status" value={stockStatus} onChange={(v) => { setStockStatus(v); setPage(1); }} allLabel="Any stock status"
                  options={PROCUREMENT_STATUS.list.map((s) => ({ value: s.value, label: s.label }))} />
              </FilterBar>
              <div className="flex flex-wrap gap-2">
                <Button icon={<Download className="h-4 w-4" />} onClick={() => void doExport()}>Export CSV</Button>
                {manage && <Button icon={<Upload className="h-4 w-4" />} onClick={() => setImporting(true)}>Import CSV</Button>}
                {manage && <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setForm({ product: null })}>New product</Button>}
              </div>
            </div>
          </div>} />

      <ProductDetailModal id={detailId} onClose={() => setDetailId(null)}
        onEdit={(p) => { setDetailId(null); setForm({ product: p }); }}
        onAdjust={(p) => setAdjust(p)}
        onDeleted={() => setDetailId(null)} />
      <ProductFormModal open={form !== null} product={form?.product ?? null} onClose={() => setForm(null)}
        categories={categories} vendors={vendors.data?.data ?? []} showCost={showsCost} onSaved={() => undefined} />
      <StockAdjustModal product={adjust} onClose={() => setAdjust(null)} />
      <ImportModal open={importing} onClose={() => setImporting(false)} />
    </div>
  );
}
